import { Trans } from '@i18n';
import {
  BuildingActiveStateFilter,
  building_state_enum,
  class_of_use_enum,
  efficiency_class_enum,
  energy_certificate_type_enum,
  energy_source_type_enum,
  eu_taxonomy_compliance_enum,
  tax_bracket_enum,
  type_of_use_enum,
} from '@predium/enums';
import {
  translateBuildingStateEnum,
  translateClassOfUseEnum,
  translateEfficiencyClassEnum,
  translateEnergyCertificateTypeEnum,
  translateEnergySourceTypeEnum,
  translateEuTaxonomyComplianceEnum,
  translateTaxBracketEnum,
  translateTypeOfUseEnum,
} from '@predium/i18n/client';
import { accessEnum } from '@predium/utils';
import { inferParserType } from 'nuqs';
import { memo } from 'react';
import useOrgPreferences from '@/hooks/useOrgPreferences';
import { BuildingFiltersKeys } from './BuildingFilters';
import { BUILDING_FILTERS_SEARCH_PARAMS, BuildingFiltersSearchParams } from './search-params';

// Most filters parse arrays, but some (e.g. `activeStateFilter`) parse a single
// enum value. Unwrap array filters to their element type, pass scalar filters through.
type FilterValue<T extends BuildingFiltersSearchParams> =
  Exclude<inferParserType<typeof BUILDING_FILTERS_SEARCH_PARAMS>[T], null | number> extends infer V
    ? V extends unknown[]
      ? V[number]
      : V
    : never;

type Props<T extends BuildingFiltersSearchParams> = {
  type: T;
  value: FilterValue<T>;
};

const FilterValueLabel = memo(<T extends BuildingFiltersSearchParams>({ type, value }: Props<T>) => {
  const { orgPreferences } = useOrgPreferences();

  switch (type) {
    case BuildingFiltersKeys.classOfUses:
      return translateClassOfUseEnum(value as class_of_use_enum);
    case BuildingFiltersKeys.typeOfUses:
      return translateTypeOfUseEnum(value as type_of_use_enum);
    case BuildingFiltersKeys.efficiencyClasses:
      return translateEfficiencyClassEnum(value as efficiency_class_enum);
    case BuildingFiltersKeys.energyCertificateTypes:
      return translateEnergyCertificateTypeEnum(value as energy_certificate_type_enum);
    case BuildingFiltersKeys.energySourceTypes:
      return translateEnergySourceTypeEnum(value as energy_source_type_enum);
    case BuildingFiltersKeys.taxBrackets:
      return translateTaxBracketEnum(value as tax_bracket_enum);
    case BuildingFiltersKeys.euTaxonomyCompliance:
      return translateEuTaxonomyComplianceEnum(
        accessEnum(eu_taxonomy_compliance_enum, String(value)),
        orgPreferences.euTaxonomyDatasource,
      );
    case BuildingFiltersKeys.buildingStates:
      return translateBuildingStateEnum(value as building_state_enum);
    case BuildingFiltersKeys.monumentProtections:
    case BuildingFiltersKeys.heritageDistrict:
    case BuildingFiltersKeys.milieuProtection:
    case BuildingFiltersKeys.leasehold:
    case BuildingFiltersKeys.districtHeatingAvailability:
    case BuildingFiltersKeys.hasConsumptionData:
    case BuildingFiltersKeys.slfCompliant:
      return value ? <Trans i18nKey="General_Yes" /> : <Trans i18nKey="General_No" />;
    case BuildingFiltersKeys.activeStateFilter:
      return value === BuildingActiveStateFilter.ONLY_INACTIVE ? (
        <Trans i18nKey="DataCollection_BuildingListFilter-showOnlyInactiveBuildings" />
      ) : (
        <Trans i18nKey="DataCollection_BuildingListFilter-hideInactiveBuildings" />
      );
    case BuildingFiltersKeys.isLod2:
      return value ? (
        <Trans i18nKey="General_LOD_DetailLevel-Detailed" />
      ) : (
        <Trans i18nKey="General_LOD_DetailLevel-Basic" />
      );
    default:
      throw new Error(`Unhandled Status Quo Analysis filter label for "${type}" type`);
  }
});

export default FilterValueLabel;
